import '@fontsource/dm-sans'
import '@fontsource/dm-sans/500.css'
import '@fontsource/dm-sans/700.css'
import '@fontsource/open-sans'
import '@fontsource/open-sans/500.css'
import '@fontsource/open-sans/700.css'
import '@fontsource/poppins'
import '@fontsource/poppins/500.css'
import '@fontsource/poppins/700.css'
import '@fontsource/poppins/900.css'
import {AlertClassKey, AlertTitleClassKey} from '@mui/material'
import {SkeletonClassKey} from '@mui/material/Skeleton'
import {createTheme, Theme} from '@mui/material/styles'

import {getTextDirection} from '@progyny/utils/rtl'

export type BrandColors = {
  primary: string
  softPink: string
  seashell: string
  middleBlue: string
  lightCoral: string
  darkGrey: string
  white: string
  powderBlue: string
  disabledGray: string
  lightGray: string
  navColor: string
}

type TreatmentColors = {
  adoption: string
  eggDonation: string
  eggFreezing: string
  insemination: string
  fertilityTesting: string
  fertilityAnalysisAndIVFPreparation: string
  ivf: string
  spermDonation: string
  spermFreezing: string
  surrogacy: string
  pgt: string
  icsi: string
  embryoFreezing: string
  embryoDonation: string
  embryoTransfer: string
  fertilityAnalysis: string
  hrt: string
  infertilityTesting: string
  iui: string
  default: string
}

export type ConstValues = {
  appHeaderHeight: number
  borderWidth: number
}

declare module '@mui/material/styles/overrides' {
  export interface ComponentNameToClassKey {
    MuiSkeleton: SkeletonClassKey
    MuiAlert: AlertClassKey
    MuiAlertTitle: AlertTitleClassKey
  }
}

declare module '@mui/material/styles/createPalette' {
  interface TypeOther {
    border: string
    backdrop: string
    snackbar: string
    info: string
    activeRating: string
    brandColors: BrandColors
    treatmentColors: TreatmentColors
    constValues: ConstValues
  }
  interface TypeText {
    muted: string
    label: string
  }

  interface Neutral {
    main: string
    contrastText: string
    30: string
    50: string
  }
  interface Palette {
    other: TypeOther
    text: TypeText
    neutral: Neutral
    tertiary: Palette['primary']
    accents: {
      mint: {
        main: string
        light: string
      }
      matcha: {
        main: string
      }
      salat: {
        main: string
      }
      purple: {
        main: string
      }
      blueberry: {
        main: string
      }
      peach: {
        main: string
      }
      minty: {
        main: string
      }
    }
  }

  interface PaletteOptions {
    other?: Partial<TypeOther>
    neutral: Neutral
    tertiary: PaletteOptions['primary']
    accents?: {
      mint?: {
        main: string
      }
      matcha: {
        main: string
      }
      salat: {
        main: string
      }
      purple?: {
        main: string
      }
      blueberry?: {
        main: string
      }
      peach?: {
        main: string
      }
      minty?: {
        main: string
      }
    }
  }

  interface SimplePaletteColorOptions {
    muted?: string
    newMain?: string
    navigation?: string
  }

  interface PaletteColor {
    muted?: string
    50?: string
  }
}

declare module '@mui/material/Button' {
  interface ButtonPropsColorOverrides {
    neutral: true
  }
}

const brandColors: BrandColors = {
  primary: '#07378F',
  softPink: '#FFD1CC',
  seashell: '#FFF3EC',
  middleBlue: '#91CED2',
  lightCoral: '#F86969',
  darkGrey: '#403F3F',
  white: '#FFFFFF',
  powderBlue: '#abe4e5',
  disabledGray: '#F3F4F6',
  lightGray: '#7B7B86',
  navColor: '#F3F6FB',
}

const treatmentColors: TreatmentColors = {
  adoption: '#FFF3EC',
  eggDonation: '#DBF8FE',
  eggFreezing: '#F3E2F9',
  insemination: '#E7EDF9',
  fertilityTesting: '#FCF1D5',
  fertilityAnalysisAndIVFPreparation: 'rgba(247, 213, 121, 0.50)',
  ivf: '#E3FAE3',
  icsi: 'rgba(227, 250, 227, 0.70)',
  spermDonation: '#FFEEDD',
  spermFreezing: '#F6B9C3',
  surrogacy: '#FBB4AF',
  pgt: '#91CED2',
  embryoFreezing: '#C6D4F3',
  embryoDonation: 'rgba(127, 154, 228, 0.70)',
  embryoTransfer: '#E2E3FF',
  fertilityAnalysis: '#BAE4E7',
  hrt: '#EBD1E2',
  infertilityTesting: '#F4CCD6',
  iui: '#BAE8C1',
  default: '#F3F4F6',
}

const constValues = {
  appHeaderHeight: 64,
  borderWidth: 3,
}

/**
 * Create a theme with dynamic direction support
 * @param languageCode - The current language code to determine direction
 * @returns MUI Theme with appropriate direction
 */
export const createDirectionalTheme = (languageCode: string = 'en'): Theme => {
  const direction = getTextDirection(languageCode)
  const isRTL = direction === 'rtl'

  return createTheme({
    spacing: 8,
    direction,
    breakpoints: {
      values: {
        xs: 0,
        sm: 540,
        md: 896,
        lg: 1280,
        xl: 1620,
      },
    },
    components: {
      MuiAppBar: {
        styleOverrides: {
          root: {
            color: brandColors.darkGrey,
          },
        },
      },
      MuiCssBaseline: {
        styleOverrides: {
          body: {
            fontSize: '1rem',
            lineHeight: 1.4,
            letterSpacing: '0.02rem',
          },
        },
      },
      MuiInput: {
        defaultProps: {
          color: 'primary',
        },
      },
      MuiAlert: {
        styleOverrides: {
          root: {
            fontWeight: '400',
          },
          filledSuccess: {
            color: '#448046',
            backgroundColor: '#E2F5E3',
            svg: {
              color: '#6ECC71',
            },
          },
          filledWarning: {
            color: '#614E1C',
            backgroundColor: '#FCEFCD',
            svg: {
              color: '#A18335',
            },
          },
          filledError: {
            color: '#631B16',
            backgroundColor: '#FEE1E1',
            svg: {
              color: '#F44336',
            },
          },
          filledInfo: {
            color: '#1B2557',
            backgroundColor: '#EDEFF9',
            svg: {
              color: '#2D3E91',
            },
          },
          standardInfo: {
            '& .MuiAlert-icon': {
              color: 'inherit',
            },
            '& .MuiAlert-message': {
              padding: '4px 0',
            },
          },
        },
      },
      MuiSelect: {
        styleOverrides: {
          select: {
            '&:focus': {
              background: 'none',
            },
          },
        },
      },
      MuiTablePagination: {
        styleOverrides: {
          root: {
            '& .MuiTablePagination-displayedRows	': {
              fontSize: 14,
            },
          },
        },
      },
      MuiAlertTitle: {
        styleOverrides: {
          root: {
            fontWeight: 700,
          },
        },
      },
      MuiDialogTitle: {
        styleOverrides: {
          root: {
            fontWeight: 600,
          },
        },
      },
      MuiTypography: {
        styleOverrides: {
          root: {letterSpacing: '0.02rem'},
        },
      },
      MuiAccordion: {
        styleOverrides: {
          root: {
            borderBottom: `solid 1px rgba(0, 0, 0, 0.12)`,
            boxShadow: 'none',
            '&::before': {
              backgroundColor: 'transparent',
            },
            '&$expanded': {
              margin: 0,
            },
          },
        },
      },
      MuiAccordionSummary: {
        styleOverrides: {
          root: {
            padding: 0,
            paddingInlineEnd: 16,
            backgroundColor: 'transparent',
          },
        },
      },
      MuiAccordionDetails: {
        styleOverrides: {
          root: {
            padding: isRTL ? '12px 0 12px 16px' : '12px 16px 12px 0',
          },
        },
      },
      MuiFormLabel: {
        styleOverrides: {
          asterisk: {
            color: brandColors.lightCoral,
          },
        },
      },
      MuiButton: {
        defaultProps: {
          color: 'primary',
        },
        styleOverrides: {
          root: {
            textTransform: 'none',
            boxShadow: 'none',
            fontFamily: 'DM Sans',
            fontWeight: 600,
            fontSize: 14,
          },
          contained: {
            '&:hover': {
              boxShadow:
                '0px 2px 4px -1px rgba(0, 0, 0, 0.2), 0px 4px 5px rgba(0, 0, 0, 0.14)',
            },
          },
          containedSecondary: {
            '&:hover': {
              backgroundColor: '#E9F5F6',
            },
          },
        },
      },
      MuiTextField: {
        styleOverrides: {
          root: {},
        },
      },
      MuiLink: {
        defaultProps: {
          color: 'primary',
          underline: 'hover',
        },
        styleOverrides: {
          underlineHover: {
            cursor: 'pointer',
          },
        },
      },
      MuiFormHelperText: {
        styleOverrides: {
          root: {
            marginTop: 2,
          },
          contained: {
            marginInlineStart: 0,
            marginInlineEnd: 0,
          },
        },
      },
      MuiCardContent: {
        styleOverrides: {
          root: {
            padding: 20,
          },
        },
      },
      MuiPopover: {
        styleOverrides: {
          paper: {
            boxShadow:
              '0px 2px 1px -1px rgba(0, 0, 0, 0.2),' +
              ' 0px 1px 1px rgba(0, 0, 0, 0.14),' +
              ' 0px 1px 3px rgba(0, 0, 0, 0.12)',
          },
        },
      },
      MuiList: {
        styleOverrides: {
          padding: {paddingTop: 0, paddingBottom: 0},
        },
      },
      MuiListItemText: {
        styleOverrides: {
          root: {
            fontSize: 16,
          },
          primary: {
            fontSize: 16,
          },
        },
      },
      MuiListItemIcon: {
        styleOverrides: {
          root: {
            minWidth: 36,
          },
        },
      },
      MuiChip: {
        styleOverrides: {
          root: {
            fontFamily: 'DM Sans',
          },
        },
      },
      MuiTooltip: {
        styleOverrides: {
          tooltip: {
            fontFamily: 'DM Sans',
            backgroundColor: '#EDEDED',
            color: '#251F1F',
            padding: 16,
          },
          arrow: {
            color: '#EDEDED',
          },
        },
      },
    },
    palette: {
      background: {
        default: brandColors.white,
      },
      primary: {
        main: brandColors.primary,
        light: '#BCC5EF',
        dark: '#081A67',
        muted: 'rgba(45, 62, 145, 0.15)',
      },
      secondary: {
        main: brandColors.middleBlue,
        light: '#BAE4E7',
        dark: '#38999F',
        muted: 'rgba(145, 206, 210, 0.15)',
        contrastText: brandColors.primary,
      },
      tertiary: {main: '#E7EDF9', contrastText: brandColors.primary},
      action: {
        hover: 'rgba(64, 63, 63, 0.08)',
      },
      text: {
        primary: '#251F1F',
        secondary: 'rgba(63, 64, 64, 0.54)', // darkGrey @ 54% opacity
        disabled: 'rgba(63, 64, 64, 0.38)', // darkGrey @ 38% opacity
        muted: '#9E9E9E',
      },
      error: {
        main: '#F44336',
      },
      warning: {
        main: '#FCF1D5',
        dark: '#A18335',
        contrastText: '#614E1C',
      },
      info: {
        main: '#F4F6FF',
      },
      success: {
        main: '#6ECC71',
        light: '#f0faf1',
      },
      neutral: {
        main: brandColors.darkGrey,
        contrastText: brandColors.white,
        30: '#7B7B86',
        50: '#F5F5F5',
      },
      accents: {
        mint: {
          main: '#38999F',
        },
        matcha: {
          main: '#DBF8FE',
        },
        salat: {
          main: '#5CB3A3',
        },
        purple: {
          main: '#B491C0',
        },
        blueberry: {
          main: '#F3E2F9',
        },
        peach: {
          main: '#4A0D0D',
        },
        minty: {
          main: '#1E393B',
        },
      },
      other: {
        brandColors,
        treatmentColors,
        constValues,
        border: 'rgba(0, 0, 0, 0.23)',
        backdrop: 'rgba(0, 0, 0, 0.04)',
      },
    },
    typography: {
      h1: {
        fontFamily: 'poppins',
        fontWeight: 700,
        fontSize: 34,
        lineHeight: 1.5,
      },
      h2: {
        fontFamily: 'poppins',
        fontWeight: 700,
        fontSize: 32,
        lineHeight: 1.4,
      },
      h3: {
        fontFamily: 'poppins',
        fontSize: 24,
        fontWeight: 600,
        lineHeight: 1.4,
      },
      h4: {
        fontFamily: 'poppins',
        fontSize: 22,
        fontWeight: 700,
        letterSpacing: 0.25,
        lineHeight: 1.4,
      },
      h5: {
        fontFamily: 'poppins',
        fontSize: 20,
        fontWeight: 500,
        lineHeight: 1.25,
        letterSpacing: '0.02rem',
      },
      h6: {
        fontFamily: 'poppins',
        fontSize: 18,
        fontWeight: 500,
        lineHeight: 1.2,
        letterSpacing: '0.02rem',
      },
      subtitle1: {
        fontFamily: 'DM Sans',
        fontWeight: 700,
        fontSize: 16,
        lineHeight: 1.25,
        letterSpacing: '0.02rem',
        color: brandColors.darkGrey,
      },
      subtitle2: {
        fontFamily: 'DM Sans',
        fontWeight: 600,
        fontSize: 14,
        lineHeight: 1.57,
        letterSpacing: 0.1,
        color: brandColors.darkGrey,
      },
      body1: {
        fontFamily: 'DM Sans',
        fontSize: 18,
        lineHeight: 1.2,
        letterSpacing: '0.02rem',
      },
      body2: {
        fontFamily: 'DM Sans',
        fontSize: 16,
        lineHeight: 1.4,
        letterSpacing: '0.02rem',
      },
      caption: {
        fontFamily: 'DM Sans',
        fontSize: 14,
        lineHeight: 1.6,
        letterSpacing: 0.4,
      },
      overline: {
        fontFamily: 'DM Sans',
      },
    },
  })
}

// Default theme (LTR)
export const theme = createDirectionalTheme('en')
